/**
 * @file Marks a weapon that is no longer carried by anybody (issue #15).
 *
 * The same component covers both halves of a weapon's life outside a holder,
 * because they are the same entity: `grounded === false` while it is flying
 * after a throw, `true` once it has hit a wall, spent its range, or been
 * dropped by a stunned enemy. Systems tell the two apart with that one flag
 * instead of shuffling components around mid-flight.
 *
 * The entity always carries the very {@link ./Weapon.js Weapon} instance its
 * holder had, so throwing and picking a weapon back up never rebuilds its stats.
 */

import { Component } from '../ecs/Component.js';

export class ThrownWeapon extends Component {
  /**
   * @param {object} [options] Setup.
   * @param {number} [options.ownerId] Entity id of whoever let go of it.
   * @param {number} [options.range] Distance it flies before dropping.
   * @param {boolean} [options.grounded] True for a weapon that starts on the floor.
   */
  constructor({ ownerId = 0, range = 0, grounded = false } = {}) {
    super();
    /** @type {number} Entity id of the thrower; never a valid impact target. */
    this.ownerId = ownerId;
    /** @type {number} Distance the throw covers before the weapon drops. */
    this.range = range;
    /** @type {number} Distance flown so far. */
    this.travelled = 0;
    /** @type {boolean} True once it has hit an enemy; a weapon only stuns once. */
    this.hasHit = false;
    /** @type {boolean} False while airborne, true while lying on the floor. */
    this.grounded = grounded;
    /**
     * Entity id of the enemy walking over to collect it, or 0 when free. Keeps
     * a whole pack from converging on the same weapon.
     * @type {number}
     */
    this.claimedBy = 0;
  }

  /** @returns {void} */
  reset() {
    this.ownerId = 0;
    this.range = 0;
    this.travelled = 0;
    this.hasHit = false;
    this.grounded = false;
    this.claimedBy = 0;
  }
}
